import {
  createElement,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type FocusEvent,
  type FormEvent,
} from 'react';
import { CORAL, foreground } from './sections/tokens';

/** One clinical or financial insight card. */
export interface ReportInsightCardData {
  /** Serif headline below the overline. */
  headline: string;
  /** Supporting paragraph. */
  body: string;
  citationUrl?: string;
  citationLabel?: string;
}

/** Insight card key matching {@link SectionInsightsDto} fields. */
export type ReportInsightCardKey = 'clinical' | 'financial';

/** Editable insight text field (maps to DTO `heading` / `body`). */
export type ReportInsightFieldKey = 'heading' | 'body';

interface ReportInsightPairProps {
  /** Clinical insight card (hidden when null). */
  clinical?: ReportInsightCardData | null;
  /** Financial insight card (hidden when null). */
  financial?: ReportInsightCardData | null;
  /** Builder preview: edit headline and body inline. */
  editable?: boolean;
  /**
   * @param card - Clinical or financial insight card.
   * @param field - Heading or body field.
   * @param value - Updated text while typing.
   */
  onFieldChange?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
  /**
   * @param card - Clinical or financial insight card.
   * @param field - Heading or body field.
   * @param value - Final text after blur.
   */
  onFieldBlur?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
}

const cardStyle = {
  background: 'var(--white)',
  border: '0.5px solid var(--midnight-25)',
  borderRadius: 12,
  padding: '2rem',
  display: 'flex',
  flexDirection: 'column' as const,
};

const headlineStyle = {
  fontFamily: 'var(--reema-font-display)',
  fontWeight: 600,
  fontSize: 'var(--reema-text-h3)',
  color: foreground,
  margin: '1rem 0 0',
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
};

const bodyStyle = {
  fontFamily: 'var(--reema-font-body)',
  fontSize: 'var(--reema-text-body)',
  color: foreground,
  margin: '1rem 0 0',
  lineHeight: 1.6,
};

interface InsightEditableTextProps {
  as: 'h3' | 'p';
  value: string;
  style: CSSProperties;
  editable: boolean;
  card: ReportInsightCardKey;
  field: ReportInsightFieldKey;
  onFieldChange?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
  onFieldBlur?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
}

/**
 * Renders insight headline/body text. In edit mode the DOM is uncontrolled while
 * focused so React re-renders do not reset the caret position.
 * @param props - Tag, display value, and optional inline edit handlers.
 */
const InsightEditableText = ({
  as,
  value,
  style,
  editable,
  card,
  field,
  onFieldChange,
  onFieldBlur,
}: InsightEditableTextProps) => {
  const ref = useRef<HTMLElement>(null);
  const isFocusedRef = useRef(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || isFocusedRef.current) {
      return;
    }
    if (element.textContent !== value) {
      element.textContent = value;
    }
  }, [value]);

  if (!editable) {
    return createElement(as, { style }, value);
  }

  return createElement(as, {
    ref,
    className: 'outline-none',
    style,
    contentEditable: true,
    suppressContentEditableWarning: true,
    onFocus: () => {
      isFocusedRef.current = true;
    },
    onInput: (event: FormEvent<HTMLElement>) => {
      onFieldChange?.(card, field, event.currentTarget.textContent ?? '');
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      isFocusedRef.current = false;
      onFieldBlur?.(
        card,
        field,
        (event.currentTarget.textContent ?? '').trim(),
      );
    },
  });
};

/**
 * Renders paired clinical and financial insight cards in a two-column grid.
 * @param props - Optional clinical and financial insight content.
 * @returns Insight grid markup or null when both cards are absent.
 */
export const ReportInsightPair = ({
  clinical,
  financial,
  editable = false,
  onFieldChange,
  onFieldBlur,
}: ReportInsightPairProps) => {
  const items = [
    clinical
      ? { key: 'clinical' as const, overline: 'CLINICAL INSIGHT', card: clinical }
      : null,
    financial
      ? { key: 'financial' as const, overline: 'FINANCIAL INSIGHT', card: financial }
      : null,
  ].filter(
    (
      item,
    ): item is {
      key: ReportInsightCardKey;
      overline: string;
      card: ReportInsightCardData;
    } => item !== null,
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className="conclusion-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: items.length > 1 ? 'repeat(2, 1fr)' : '1fr',
        gap: '1.5rem',
        alignItems: 'stretch',
      }}
    >
      {items.map(({ key, overline, card }) => (
        <article
          key={key}
          className={`report-insight-card report-insight-card--${key}`}
          style={cardStyle}
        >
          <span className="overline" style={{ margin: 0 }}>
            {overline}
          </span>
          <InsightEditableText
            as="h3"
            value={card.headline}
            style={headlineStyle}
            editable={editable}
            card={key}
            field="heading"
            onFieldChange={onFieldChange}
            onFieldBlur={onFieldBlur}
          />
          <InsightEditableText
            as="p"
            value={card.body}
            style={bodyStyle}
            editable={editable}
            card={key}
            field="body"
            onFieldChange={onFieldChange}
            onFieldBlur={onFieldBlur}
          />
          {card.citationUrl ? (
            <a
              href={card.citationUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: 'var(--reema-font-body)',
                fontWeight: 500,
                fontSize: 14,
                color: CORAL,
                marginTop: 'auto',
                paddingTop: '1.5rem',
                textDecoration: 'none',
                alignSelf: 'flex-start',
              }}
            >
              {card.citationLabel ?? 'Citation'}
            </a>
          ) : null}
        </article>
      ))}
    </div>
  );
};

type InsightSnapshotItem = {
  heading: string;
  body: string;
  citationUrl?: string;
  citationLabel?: string;
};

/**
 * Reads one insight card from a section snapshot's `insights` object.
 * @param data - Section snapshot JSON.
 * @param key - Clinical or financial insight key.
 * @returns Card data or null when missing.
 */
export const readReportInsightCard = (
  data: Record<string, unknown>,
  key: ReportInsightCardKey,
): ReportInsightCardData | null => {
  const insights = data.insights as
    | Record<string, InsightSnapshotItem>
    | undefined;
  const item = insights?.[key];
  if (!item?.heading || !item?.body) {
    return null;
  }
  return {
    headline: item.heading,
    body: item.body,
    citationUrl: item.citationUrl,
    citationLabel: item.citationLabel,
  };
};

/**
 * Maps a DTO insight item to preview card data.
 * @param item - Clinical or financial insight from section or snapshot.
 * @returns Card data or null when heading/body are missing.
 */
export const toReportInsightCardData = (
  item:
    | {
        heading?: string;
        body?: string;
        citationUrl?: string;
        citationLabel?: string;
      }
    | undefined,
): ReportInsightCardData | null => {
  if (!item) {
    return null;
  }
  return {
    headline: item.heading ?? '',
    body: item.body ?? '',
    citationUrl: item.citationUrl,
    citationLabel: item.citationLabel,
  };
};

/** Empty insight card for builder authoring when toggles are on but copy is absent. */
export const EMPTY_REPORT_INSIGHT_CARD: ReportInsightCardData = {
  headline: '',
  body: '',
};
